import React from "react";
import { IconButton, Tooltip, Typography } from "@mui/material";
import ModeEditIcon from "@mui/icons-material/ModeEdit";

import { useTranslation } from "@/lib/i18n";
import { If } from "@/features/shared/components";
import {
  CenteredContent,
  PageContainer,
  PageHeader,
  AddFab,
  PageManagementDragAndDrop,
  TreeData,
} from "@/features/admin/components";
import { useAdminDashboard } from "@/features/admin/hooks";
import { useDashboardNavigationElements } from "@/features/admin/hooks/queries";
import {
  CreateNaivgationElementDialog,
  OverviewItem,
  CancelSaveFab,
  createTreeDataFromNavigationElements,
  createOrderArrayFromTreeData,
  useUpdateNavigationElementsOrderMutation,
} from "@/features/admin/features/pages-management";
import WarningPageOverviewItem from "@/features/admin/features/pages-management/components/warning-page-overview-item";
import type { NavigationElement } from "@/lib/api/types";
import {
  isBuiltInPageKind,
  isFolderNavigationElement,
  navigationElementKind,
  withoutHiddenWarningPages,
} from "@/lib/navigation-element-kinds";
import { useWarningPageNavLabel } from "@/features/shared/hooks/use-warning-page-defaults";
import { useWarningPageVisibility } from "@/features/admin/hooks/use-warning-page-visibility";
import { withHiddenElementsInOrder } from "@/features/admin/features/pages-management/helpers";

type TreeDataNavigationElements = TreeData<NavigationElement>;

/** Stable fallback so the tree effect doesn't re-run every render while loading. */
const EMPTY_ELEMENTS: NavigationElement[] = [];

const PagesOverviewPage = () => {
  const { t } = useTranslation();
  const navLabel = useWarningPageNavLabel();
  const dashboard = useAdminDashboard();
  const navigationElementsQuery = useDashboardNavigationElements(dashboard.id);
  const allNavigationElements = navigationElementsQuery.data ?? EMPTY_ELEMENTS;
  const visibility = useWarningPageVisibility(dashboard.id);
  const navigationElements = React.useMemo(
    () => withoutHiddenWarningPages(allNavigationElements, visibility),
    [allNavigationElements, visibility.publicWarningsPage, visibility.maritimeWarningsPage],
  );
  const updateNavigationElementOrder = useUpdateNavigationElementsOrderMutation();

  const [editOrder, setEditOrder] = React.useState(false);
  const [showNewPageDialog, setShowNewPageDialog] = React.useState(false);
  const [treeData, setTreeData] = React.useState<TreeDataNavigationElements | undefined>();

  const initialTreeData = React.useRef<TreeDataNavigationElements>();

  React.useEffect(() => {
    // Don't update the page while changing the order unless this is the first load
    if (!editOrder || !treeData) {
      const next = createTreeDataFromNavigationElements(navigationElements);

      setTreeData(next);

      initialTreeData.current = next;
    }
  }, [navigationElements]);

  const handleSaveOrder = () => {
    if (!treeData) return;

    setEditOrder(false);

    updateNavigationElementOrder.mutate(
      {
        dashboardId: dashboard.id,
        data: withHiddenElementsInOrder(
          createOrderArrayFromTreeData(treeData),
          allNavigationElements,
        ),
      },
      {
        onSuccess: () => {
          // Update the initial tree data to the new order. This is needed to undo the changes later on.
          initialTreeData.current = treeData;
        },
      },
    );
  };

  const handleCancelReorder = () => {
    setEditOrder(false);
    setTreeData(initialTreeData.current);
  };

  const availableFolders = React.useMemo(
    () => navigationElements.filter(isFolderNavigationElement),
    [navigationElements],
  );

  return (
    <>
      <PageContainer>
        <PageHeader title={t("admin.pages.overview.title")}>
          <Tooltip
            key="edit"
            onClick={() => setEditOrder(true)}
            title={t("admin.pages.overview.change_order")}
          >
            <IconButton aria-label="delete">
              <ModeEditIcon />
            </IconButton>
          </Tooltip>
        </PageHeader>

        <CenteredContent>
          <If condition={treeData}>
            <PageManagementDragAndDrop<NavigationElement>
              treeData={treeData}
              dragAndDropEnabled={editOrder}
              onChange={(newTreeData) => setTreeData(newTreeData)}
              canDrop={(dragSource, dropTarget) =>
                !(isBuiltInPageKind(navigationElementKind(dragSource?.data)) && dropTarget)
              }
              renderItem={(node, { isOpen, onToggle, dragAndDropEnabled }) => {
                if (node.data && isBuiltInPageKind(navigationElementKind(node.data))) {
                  return (
                    <WarningPageOverviewItem
                      label={navLabel(node.data)}
                      navigationElement={node.data}
                      dragAndDropEnabled={dragAndDropEnabled}
                    />
                  );
                }

                return (
                  <OverviewItem
                    dragAndDropEnabled={dragAndDropEnabled}
                    node={node}
                    isOpen={isOpen}
                    onToggle={onToggle}
                  />
                );
              }}
            />
          </If>
          <If
            condition={navigationElementsQuery.isSuccess && navigationElements.length === 0}
          >
            <Typography color="grey.600" variant="h6">
              {t("admin.pages.overview.no_pages")}
            </Typography>
          </If>
        </CenteredContent>
      </PageContainer>

      <CreateNaivgationElementDialog
        availableFolders={availableFolders}
        handleClose={() => setShowNewPageDialog(false)}
        open={showNewPageDialog}
      />

      <If condition={editOrder}>
        <CancelSaveFab onCancel={handleCancelReorder} onSave={handleSaveOrder} canSave />
      </If>

      <If condition={!editOrder}>
        <AddFab
          onClick={() => setShowNewPageDialog(true)}
          tooltip={t("admin.pages.overview.add_page")}
        />
      </If>
    </>
  );
};

export default PagesOverviewPage;
